import { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ADMIN_TOKEN_KEY = 'feel_umbria_admin_token';

const AdminAuthContext = createContext(null);

export const AdminAuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [admin, setAdmin] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Check for existing token on mount
    const token = localStorage.getItem(ADMIN_TOKEN_KEY);
    if (token) {
      verifyToken(token);
    } else {
      setIsLoading(false);
    }
  }, []);

  const verifyToken = async (token) => {
    try {
      const response = await axios.get(`${API}/admin/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAdmin(response.data);
      setIsAuthenticated(true);
      // Set default header for all future requests
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } catch (err) {
      // Token invalid or expired
      localStorage.removeItem(ADMIN_TOKEN_KEY);
      setIsAuthenticated(false);
      setAdmin(null);
    } finally {
      setIsLoading(false);
    }
  };

  const login = async (username, password) => {
    setError(null);
    try {
      const response = await axios.post(`${API}/admin/auth/login`, {
        username,
        password
      });
      
      const { token } = response.data;
      localStorage.setItem(ADMIN_TOKEN_KEY, token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      
      setAdmin({ username });
      setIsAuthenticated(true);
      return true;
    } catch (err) {
      const message = err.response?.data?.detail || 'Credenziali non valide';
      setError(message);
      return false;
    }
  };

  const logout = () => {
    localStorage.removeItem(ADMIN_TOKEN_KEY);
    delete axios.defaults.headers.common['Authorization'];
    setIsAuthenticated(false);
    setAdmin(null);
  };

  return (
    <AdminAuthContext.Provider value={{
      isAuthenticated,
      isLoading,
      admin,
      error,
      login,
      logout
    }}>
      {children}
    </AdminAuthContext.Provider>
  );
};

export const useAdminAuth = () => {
  const context = useContext(AdminAuthContext);
  if (!context) {
    throw new Error('useAdminAuth must be used within AdminAuthProvider');
  }
  return context;
};
